import React from "react";
import { useRouterState } from "@tanstack/react-router";

import { FilterableSuppliers } from "components/suppliers/FilterableSuppliers";
import { BASELINE_SUPPLIER_COLUMNS, SupplierFilters } from "components/suppliers/utils";
import { useCompetitorPartnerInfoOnBuyer } from "lib/hooks/api/organisations/useCompetitorPartnerInfoOnBuyer";
import { BuyerSummary } from "lib/types/models";
import { SecondaryTab } from "./types";

type Props = { buyer: BuyerSummary };

function getSelectedTab(url: string) {
  switch (url) {
    case SecondaryTab.PARTNERS:
      return SecondaryTab.PARTNERS;
    case SecondaryTab.COMPETITORS:
      return SecondaryTab.COMPETITORS;
    default:
      return SecondaryTab.SUPPLIERS;
  }
}

export function SupplierTab({ buyer }: Props) {
  // Subscribe to pathname for reactivity.
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const buyerBasePath = `/buyers/${buyer.guid}`;
  const location = pathname.startsWith(buyerBasePath)
    ? pathname.slice(buyerBasePath.length) || "/"
    : "/";

  const DEFAULT_BUYER_SUPPLIER_FILTERS: SupplierFilters = {
    buyerIds: [buyer.guid],
    text: "",
    sort: { field: "awards_count", order: "DESC" },
  };
  const isTabSelected = (...targetTab: SecondaryTab[]) => {
    const currentLoc = getSelectedTab(location);
    return targetTab.includes(currentLoc);
  };

  const { activeCompetitorGuids, activePartnerGuids } = useCompetitorPartnerInfoOnBuyer(
    buyer.guid,
    { enabled: isTabSelected(SecondaryTab.PARTNERS, SecondaryTab.COMPETITORS) },
  );

  const currentSelectedTabFilters = (): SupplierFilters => {
    const loc = getSelectedTab(location);

    switch (loc) {
      case SecondaryTab.COMPETITORS: {
        return {
          ...DEFAULT_BUYER_SUPPLIER_FILTERS,
          guids: activeCompetitorGuids,
        };
      }

      case SecondaryTab.PARTNERS: {
        return {
          ...DEFAULT_BUYER_SUPPLIER_FILTERS,
          guids: activePartnerGuids,
        };
      }

      default: {
        return { ...DEFAULT_BUYER_SUPPLIER_FILTERS, buyerIds: [buyer.guid] }; // get all suppliers linked to buyer
      }
    }
  };

  return (
    <FilterableSuppliers
      defaultFilters={currentSelectedTabFilters()}
      view={getSelectedTab(location)}
      hiddenFilterSections={["buyers", "lots"]}
      defaultColumns={BASELINE_SUPPLIER_COLUMNS}
    />
  );
}
